import { navigate } from "gatsby";
import * as React from "react";
import { scroller } from "react-scroll";
import * as queryString from "query-string";
import { CoinListItemResult } from "../../../../types/api-results/coin-list-item";
import { useCoinList } from "../../../hooks/query-hooks/use-coin-list";
import { SiteContext } from "../../../site-context/site-context";
import { EventCategory } from "../../../utils/analytics";
import { buildCoinUrl } from "../../../utils/build-url";
import {
  formatBigCurrency,
  formatCurrency,
} from "../../../utils/format-number";
import Actionable from "../../ui-library/actionable/actionable";
import { Card } from "../../ui-library/cards/card";
import Sparkline from "../../ui-library/charts/sparkline";
import Image from "../../ui-library/image/image";
import LoadingState from "../../ui-library/loading-state/loading-state";
import Paginator from "../../ui-library/paginator/paginator";
import { Table, TBody, THead } from "../../ui-library/table/table";
import TrendText from "../../ui-library/text/trend-text";
import styles from "./coin-list-table.styles";

const COIN_TABLE_ID = "coinListTable";

interface CoinListTableProps {
  isDefi?: boolean;
  staticData?: CoinListItemResult[];
  ids?: string[];
}

const CoinListTable: React.FC<CoinListTableProps> = ({
  isDefi,
  staticData,
  ids,
}) => {
  const {
    currency: [currency],
    usdExchangeRate,
    isMobile,
  } = React.useContext(SiteContext);

  const [page, setPage] = React.useState<number>(1);

  React.useEffect(() => {
    if (typeof window !== undefined) {
      const queryParams = queryString.parse(window.location.search);
      const pageFromUrl = queryParams.page ? Number(queryParams.page) : 1;
      setPage(pageFromUrl);
    }
  }, []);

  const { data, liveDataInitialLoading } = useCoinList({
    staticData,
    pageSize: 100,
    page,
    ids,
    hasChange: true,
    isDefi,
  });

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    navigate(`?page=${pageNumber}`);
    scroller.scrollTo(COIN_TABLE_ID, {
      smooth: true,
      offset: -50,
    });
  };

  const pageCountById = ids ? Math.ceil(ids.length / 100) : undefined;
  const totalPageCount = pageCountById || (isDefi ? 3 : 50);

  const eventCategory = EventCategory.CoinList;

  return (
    <Card id={COIN_TABLE_ID}>
      <div css={styles.root}>
        {liveDataInitialLoading && <LoadingState cover={true} />}
        <Table>
          <THead>
            {!isMobile && <th css={styles.colAlignment[0]}>#</th>}
            <th css={styles.colAlignment[1]}>Name</th>
            <th css={styles.colAlignment[2]}>Price</th>
            {!isMobile && <th css={styles.colAlignment[3]}>24h</th>}
            {!isMobile && <th css={styles.colAlignment[4]}>7d</th>}
            <th css={styles.colAlignment[5]}>Last 7 days</th>
            {isMobile && <th css={styles.colAlignment[4]}>7d</th>}
            <th css={styles.colAlignment[6]}>Market Cap</th>
          </THead>
          <TBody>
            {data?.map((row) => (
              <Actionable
                key={row.id}
                component="tr"
                role="link"
                ariaLabel={row.name}
                onClick={() => navigate(buildCoinUrl(row.slug))}
                analytics={{
                  category: eventCategory,
                  action: "Click - Coin table row",
                  label: `${row.symbol.toUpperCase()} - ${row.name}`,
                }}
              >
                {!isMobile && (
                  <td css={[styles.colAlignment[0], styles.rank]}>
                    {row.marketCapRank}
                  </td>
                )}
                <td css={[styles.colAlignment[1]]}>
                  <div css={styles.nameContainer}>
                    <div css={styles.nameColLeft}>
                      <div css={styles.imageContainer}>
                        <Image src={row.image} css={styles.image} alt="logo" />
                      </div>
                      <div css={styles.nameColRank}>{row.marketCapRank}</div>
                    </div>
                    <div>
                      <a
                        css={styles.name}
                        href={buildCoinUrl(row.slug)}
                        onClick={(e) => e.preventDefault()}
                      >
                        {row.name}
                      </a>
                      <div css={styles.symbol}>{row.symbol.toUpperCase()}</div>
                    </div>
                  </div>
                </td>
                <td css={[styles.colAlignment[2], styles.label]}>
                  {row.currentPrice
                    ? formatCurrency({
                        value: row.currentPrice * (usdExchangeRate || 1),
                        currency,
                      })
                    : "-"}

                  {isMobile && (
                    <TrendText
                      value={row.priceChangePercentage24hInCurrency}
                      isColored={true}
                      css={{ justifyContent: "flex-end" }}
                      arrowSpacing={3}
                      arrowSize={{ width: 7, height: 6 }}
                    />
                  )}
                </td>
                {!isMobile && (
                  <td css={[styles.colAlignment[3], styles.label]}>
                    <TrendText
                      value={row.priceChangePercentage24hInCurrency}
                      isColored={true}
                      css={{ justifyContent: "flex-end" }}
                      arrowSpacing={6}
                    />
                  </td>
                )}
                {!isMobile && (
                  <td css={[styles.colAlignment[4], styles.label]}>
                    <TrendText
                      value={row.priceChangePercentage7dInCurrency}
                      isColored={true}
                      css={{ justifyContent: "flex-end" }}
                      arrowSpacing={6}
                    />
                  </td>
                )}
                <td css={[styles.colAlignment[5], styles.label]}>
                  <Sparkline
                    price={row.sparklineIn7d?.price}
                    changePercent={row.priceChangePercentage7dInCurrency}
                  />
                </td>
                {isMobile && (
                  <td css={[styles.colAlignment[4], styles.label]}>
                    <TrendText
                      value={row.priceChangePercentage7dInCurrency}
                      isColored={true}
                      css={{ justifyContent: "flex-end" }}
                      arrowSpacing={3}
                      arrowSize={{ width: 7, height: 6 }}
                    />
                  </td>
                )}
                <td css={[styles.colAlignment[6], styles.label]}>
                  {row.marketCap
                    ? formatBigCurrency({ value: row.marketCap, currency })
                    : "-"}
                </td>
              </Actionable>
            ))}
          </TBody>
        </Table>
      </div>

      {totalPageCount > 1 && (
        <div css={styles.tableFooter}>
          <Paginator
            totalPageCount={totalPageCount}
            currentPageNumber={page}
            maxPagesToDisplay={isMobile ? 5 : 7}
            onPageChange={handlePageChange}
            eventCategory={eventCategory}
          />
        </div>
      )}
    </Card>
  );
};

export default CoinListTable;
